// context/HamburgerMenuContext.js
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ClientService from '../Services/client.service.js';
import ChatApiService from '../Services/chat.service.js';
import AuthService from '../Services/auth.service.js';
import Constants from '../assests/Constants/constants';import PCNService from '../Services/pcn.service.js';

const HamburgerMenuContext = createContext();

export const useHamburgerMenu = () => useContext(HamburgerMenuContext);

export const HamburgerMenuProvider = ({ children }) => {
  const [menuProps, setMenuProps] = useState({});
  const [onDocumentRowClick, setOnDocumentRowClick] = useState(null);
  const { t } = useTranslation();

  /* ---- STATE & SETTERS -------------------------------------------------- */
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  /** Optional array of { id, docType } for EditModal next/prev when opening from UnprocessedItems etc. */
  const [editModalDataSource, setEditModalDataSource] = useState(null);
  const [isJustEditModalOpen, setIsJustEditModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [pcnIncludeStornoChecks, setPcnIncludeStornoChecks] = useState(null);
  const [editModalUpdated, setEditModalUpdated] = useState(false);

  /* ---- Reference Sequence View Modal -------------------------------- */
  const [referenceSequenceModalOpen, setReferenceSequenceModalOpen] = useState(false);

  const openReferenceSequenceModal = useCallback(() => {
    setReferenceSequenceModalOpen(true);
  }, []);

  const closeReferenceSequenceModal = useCallback(() => {
    setReferenceSequenceModalOpen(false);
  }, []);

  /* ---- References Tree Modal (edit document reference numbers) -------- */
  const [referencesModalOpen, setReferencesModalOpen] = useState(false);

  const openReferencesModal = useCallback(() => {
    setReferencesModalOpen(true);
  }, []);

  const closeReferencesModal = useCallback(() => {
    setReferencesModalOpen(false);
  }, []);

  /* ---- PCN STATE -------------------------------------------------- */
  const [showPCNModal, setShowPCNModal] = useState(false);
  const [showPCNChecksModal, setShowPCNChecksModal] = useState(false);
  const [pcnHighConfirmationDocs, setPcnHighConfirmationDocs] = useState([]);
  const [pcnDocumentsNeedingAllocation, setPcnDocumentsNeedingAllocation] = useState([]);
  const [pcnDocumentsWithoutReference, setPcnDocumentsWithoutReference] = useState([]);
  const [pcnDocumentsOutOfRange, setPcnDocumentsOutOfRange] = useState([]);
  const [pcnChecksLoading, setPcnChecksLoading] = useState(false);
  const [pcnReportDetails, setPcnReportDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pcnCheckDates, setPcnCheckDates] = useState(null); // Store dates used for PCN check

  /* ---- CHAT STATE -------------------------------------------------- */
  const [openChat, setOpenChat] = useState(false);
  const [chatKey, setChatKey] = useState(Date.now());
  const [targetUserId, setTargetUserId] = useState('');
  const [targetUserType, setTargetUserType] = useState('');
  const [targetUserName, setTargetUserName] = useState('');
  const [clarificationAttachContext, setClarificationAttachContext] = useState(null);

  /* ---- ENUMS REFRESH CALLBACK -------------------------------------------------- */
  const [refreshEnumsCallback, setRefreshEnumsCallback] = useState(null);
  
  /* ---- ENUMS DATA -------------------------------------------------- */
  const [accountCardOptions, setAccountCardOptions] = useState([]);
  const [sortCodeIdOptions, setSortCodeIdOptions] = useState([]);

  /* ---- PCN REFRESH CALLBACK -------------------------------------------------- */
  const [onPCNRefresh, setOnPCNRefresh] = useState(null);
  const [pcnRefreshClientDetails, setPcnRefreshClientDetails] = useState(false);
  const [pcnRefreshDetails, setPcnRefreshDetails] = useState(false);

  /* ---- UnprocessedItems: refresh list after edit/PA update (keep modal open) ---- */
  const refreshUnprocessedPreviewRef = useRef(null);
  const setRefreshUnprocessedPreview = useCallback((fn) => { refreshUnprocessedPreviewRef.current = fn; }, []);
  const refreshUnprocessedPreview = useCallback(() => { refreshUnprocessedPreviewRef.current?.(); }, []);

  /* ---- Withholding tax modal (opened from HamburgerMenu or Document Actions) ---- */
  const openWithholdingTaxModelRef = useRef(null);
  const setOpenWithholdingTaxModel = useCallback((fn) => {
    openWithholdingTaxModelRef.current = fn;
  }, []);
  const openWithholdingTaxModel = useCallback((reportType) => {
    openWithholdingTaxModelRef.current?.(reportType);
  }, []);


  /* ---- PCN UTILITY FUNCTIONS -------------------------------------------------- */
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const setDates = (isRange, date, startDate, endDate) => {
    if (isRange) {
      return { start: startDate?.toDate?.() || startDate, end: endDate?.toDate?.() || endDate };
    } else {
      return { start: date?.toDate?.() || date, end: date?.toDate?.() || date };
    }
  };


  /* ---- PCN FUNCTIONS -------------------------------------------------- */
  const downloadPCNReport = useCallback(async () => {
    setShowPCNModal(true);
  }, []);

  const handlePCNDownload = useCallback(async (modalStartDate = null, modalEndDate = null) => {
    setLoading(true);

    const { client, token, isRange, date, startDate, endDate } = menuProps;



    // Use modal dates if provided, otherwise use parent component dates
    let start, end;
    if (modalStartDate && modalEndDate) {
      start = modalStartDate;
      end = modalEndDate;
    } else {
      // Dates are the same for both files, so calculate once
      const dateResult = setDates(isRange, date, startDate, endDate);
      start = dateResult.start;
      end = dateResult.end;
    }

    /* ───────────── TXT file first ───────────── */
    try {
      await PCNService.DownloadPCNReport(
        client.caseId,
        start,
        end,
        pcnIncludeStornoChecks,
        token
      );
      toast.success(t("reportDownloadSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("reportDownloadFailure"));
    }

    // Nice little gap so two "Save as…" dialogs don't overlap
    await delay(500);

    /* ───────────── PDF file second ───────────── */
    try {
      await PCNService.DownloadPCNPDF(
        client.caseId,
        start,
        end,
        pcnIncludeStornoChecks,
      );
      toast.success(t("reportDownloadSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("reportDownloadFailure"));
    }

    // House‑keeping
    setLoading(false);
    setShowPCNModal(false);
    setPcnIncludeStornoChecks(null);
  }, [menuProps, pcnIncludeStornoChecks, t]);

  const refreshPCNData = useCallback(async () => {
    const { client, token, isRange, date, startDate, endDate } = menuProps;

    if (client && token) {
      // Use stored PCN check dates if available, otherwise use menuProps dates
      let start, end;
      if (pcnCheckDates) {
        start = pcnCheckDates.start;
        end = pcnCheckDates.end;
      } else {
        const dates = setDates(isRange, date, startDate, endDate);
        start = dates.start;
        end = dates.end;
      }

      try {
        const response = await PCNService.CheckPCNDocuments(
          client.caseId,
          start,
          end,
          pcnIncludeStornoChecks
        );

        if (response.success) {
          setPcnHighConfirmationDocs(response.documents || []);
          setPcnDocumentsNeedingAllocation(response.documentsNeedingAllocation || []);
          setPcnDocumentsWithoutReference(response.documentsWithoutReference || []);
          setPcnDocumentsOutOfRange(response.documentsOutOfRange || []);

          // Set the new PCN report details
          if (response.incomes || response.expenses || response.equipmentTransactions) {
            setPcnReportDetails({
              incomes: response.incomes || [],
              expenses: response.expenses || [],
              equipmentTransactions: response.equipmentTransactions || [],
              clientName: client.name,
              incomeType: response.incomeType || {},
              expenseType: response.expenseType || {},
              incomeVatSum: response.incomeVatSum,
              incomeSum: response.incomeSum,
              expenseVatSum: response.expenseVatSum,
              expenseSum: response.expenseSum,
              equipmentInputVat: response.equipmentInputVat,
              equipmentInputSum: response.equipmentInputSum,
              amountToPayOrRefund: response.amountToPayOrRefund,
            });
          }
        }
      } catch (error) {
        console.error('Error refreshing PCN data:', error);
      }
      setPcnRefreshDetails(false);
    }
  }, [menuProps, pcnIncludeStornoChecks, pcnCheckDates]);

  const handlePCNChecks = useCallback(async (modalStartDate = null, modalEndDate = null) => {
    setPcnChecksLoading(true);

    const { client, token, isRange, date, startDate, endDate } = menuProps;


    try {
      // Use modal dates if provided, otherwise use parent component dates
      let start, end;


      if (modalStartDate && modalEndDate) {
        start = modalStartDate;
        end = modalEndDate;
      } else {
        const { start: parentStart, end: parentEnd } = setDates(isRange, date, startDate, endDate);
        start = parentStart;
        end = parentEnd;
      }

      // Store the dates used for this PCN check so refreshes use the same dates
      setPcnCheckDates({ start, end });

      const response = await PCNService.CheckPCNDocuments(
        client.caseId,
        start,
        end,
        pcnIncludeStornoChecks
      );

      if (response.success) {
        setPcnHighConfirmationDocs(response.documents || []);
        setPcnDocumentsNeedingAllocation(response.documentsNeedingAllocation || []);
        setPcnDocumentsWithoutReference(response.documentsWithoutReference || []);
        setPcnDocumentsOutOfRange(response.documentsOutOfRange || []);

        // Set the new PCN report details
        if (response.incomes || response.expenses || response.equipmentTransactions) {
          setPcnReportDetails({
            incomes: response.incomes || [],
            expenses: response.expenses || [],
            equipmentTransactions: response.equipmentTransactions || [],
            clientName: client.name,
            incomeType: response.incomeType || {},
            expenseType: response.expenseType || {},
            incomeVatSum: response.incomeVatSum,
            incomeSum: response.incomeSum,
            expenseVatSum: response.expenseVatSum,
            expenseSum: response.expenseSum,
            equipmentInputVat: response.equipmentInputVat,
            equipmentInputSum: response.equipmentInputSum,
            amountToPayOrRefund: response.amountToPayOrRefund,
          });
        }

        setShowPCNChecksModal(true);
      } else {
        toast.success(t("NoHighConfirmationDocuments"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("PCNChecksFailed"));
    } finally {
      setPcnChecksLoading(false);
    }
  }, [menuProps, pcnIncludeStornoChecks, t]);

  // Set the PCN refresh callback in the context
  // useEffect(() => {

  //   setOnPCNRefresh(() => refreshPCNData);
  // }, [setOnPCNRefresh, refreshPCNData]);

  useEffect(() => {
 
    if (pcnRefreshDetails && showPCNChecksModal) {
      refreshPCNData();
    }
  }, [pcnRefreshDetails, showPCNChecksModal]);

  /* ---- HANDLERS -------------------------------------------------- */
  const handleDocumentRowClick = useCallback(
    (documentId, sectionKey, dataSource = null) => {
      if (!documentId || !sectionKey) {
        return;
      }

      setSelectedDocumentId(documentId);
      setSelectedSectionKey(sectionKey);
      setEditModalDataSource(Array.isArray(dataSource) ? dataSource : null);
      setIsJustEditModalOpen(true);   // open the edit modal
      setIsSearchModalOpen(true);     // keep the search modal open
    },
    []
  );

  /* ---- CHAT HANDLERS -------------------------------------------------- */
  const handleChatClose = useCallback(() => {
    setOpenChat(false);
    setChatKey(Date.now());
    setTargetUserId('');
    setTargetUserType('');
    setTargetUserName('');
    setClarificationAttachContext(null);
  }, []);

  const handleSendMessageToChat = useCallback(async (file, clientCaseId, clientName) => {
    try {
      // Get current user information
      const currentUserId = await AuthService.getCurrentUserTypeId();
      const currentUserType = await AuthService.getCurrentUserType();
      const senderName = await AuthService.getCurrentUserTypeName();

      // Set target user to the client (caseId)
      const targetUserId = clientCaseId;
      const targetUserType = Constants.ReceiverType.Client; // Client type
      const targetUserName = clientName; // You might want to get the actual client name

      // Create FormData for the API
      const formData = new FormData();
      formData.append("SenderId", currentUserId);
      formData.append("SenderName", senderName);
      formData.append("SenderType", currentUserType);
      formData.append("ReceiverId", targetUserId);
      formData.append("ReceiverType", targetUserType);
      formData.append("MessageType", "2"); // File type
      formData.append("MessageContent", "");

      // Add the file
      formData.append("Files", file, file.name);

      // Send the message
      const response = await ChatApiService.sendMessage(formData);

      if (response) {
        // Open chat with the target user using the same logic as selectUser

        setTargetUserId(targetUserId);
        setTargetUserType(targetUserType);
        setTargetUserName(targetUserName);
        setOpenChat(true);
        setChatKey(Date.now());

        toast.success(t('FileSentToChat'));
      }
    } catch (error) {
      console.error('Error sending file to chat:', error);
      toast.error(t('ErrorSendingFileToChat'));
    }
  }, [t]);

  // Send text message (for links, etc.)
  const handleSendTextMessageToChat = useCallback(async (message, clientCaseId, clientName) => {
    try {
      console.log('🔵 handleSendTextMessageToChat - Input:', {
        message,
        messageType: typeof message,
        messageLength: message?.length,
        clientCaseId,
        clientName
      });

      // Get current user information
      const currentUserId = await AuthService.getCurrentUserTypeId();
      const currentUserType = await AuthService.getCurrentUserType();
      const senderName = await AuthService.getCurrentUserTypeName();

      // Set target user to the client (caseId)
      const targetUserId = clientCaseId;
      const targetUserType = Constants.ReceiverType.Client; // Client type
      const targetUserName = clientName;

      console.log('🔵 Sender/Receiver info:', {
        currentUserId,
        senderName,
        currentUserType,
        targetUserId,
        targetUserName,
        targetUserType
      });

      // Create FormData for the API
      const formData = new FormData();
      formData.append("SenderId", currentUserId);
      formData.append("SenderName", senderName);
      formData.append("SenderType", currentUserType);
      formData.append("ReceiverId", targetUserId);
      formData.append("ReceiverType", targetUserType);
      formData.append("MessageType", "0"); // ✅ Text type = 0 (not 1!)
      formData.append("MessageContent", message);

      // Log FormData
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }

      // Send the message
      const response = await ChatApiService.sendMessage(formData);
      
      // Check if response contains message data
      if (response) {
        console.log('📨 Response details:', {
          status: response.status,
          data: response.data,
          message: response.message
        });
        
        // Open chat with the target user
        setTargetUserId(targetUserId);
        setTargetUserType(targetUserType);
        setTargetUserName(targetUserName);
        setOpenChat(true);
        setChatKey(Date.now());

        toast.success(t('MessageSentToChat'));
      } else {
        console.error('❌ No response from chat API');
        toast.error('Failed to send message - no response');
      }
    } catch (error) {
      console.error('Error sending message to chat:', error);
      toast.error(t('ErrorSendingMessageToChat'));
    }
  }, [t]);

  // New function to select user and open chat (similar to selectUser in Chat.jsx)
  const selectUserAndOpenChat = useCallback(async (clientCaseId, clientName, attachContext = null) => {
    try {
      // Get current user information
      const currentUserId = await AuthService.getCurrentUserTypeId();
      const currentUserType = await AuthService.getCurrentUserType();

      // Set target user information
      const targetUserId = clientCaseId;
      const targetUserType = 1; // Client type
      const targetUserName = clientName;

      // Set the target user (similar to selectUser logic)
      setTargetUserId(targetUserId);
      setTargetUserType(targetUserType);
      setTargetUserName(targetUserName);
      setClarificationAttachContext(attachContext);

      // Check if user is online
      const isSelectedUserOnline = await ChatApiService.checkUserOnlineStatus(targetUserId);
      
      // Read any unread messages
      await ChatApiService.readMessage(currentUserId, currentUserType, targetUserId, targetUserType);

      // Open the chat
      setOpenChat(true);
      setChatKey(Date.now());

    } catch (error) {
      console.error('Error selecting user and opening chat:', error);
      toast.error(t('ErrorOpeningChat'));
    }
  }, [t]);

  return (
    <HamburgerMenuContext.Provider value={{
      menuProps,
      setMenuProps,
      onDocumentRowClick,
      setOnDocumentRowClick,
      selectedDocumentId,
      selectedSectionKey,
      isJustEditModalOpen,
      isSearchModalOpen,
      /* setters (export only if outside code really needs them) */
      setSelectedDocumentId,
      setSelectedSectionKey,
      editModalDataSource,
      setEditModalDataSource,
      setIsJustEditModalOpen,
      setIsSearchModalOpen,
      /* PCN refresh callback */
      onPCNRefresh,
      setOnPCNRefresh,
      /* PCN state and functions */
      showPCNModal,
      setShowPCNModal,
      showPCNChecksModal,
      setShowPCNChecksModal,
      pcnHighConfirmationDocs,
      setPcnHighConfirmationDocs,
      pcnDocumentsNeedingAllocation,
      setPcnDocumentsNeedingAllocation,
      pcnDocumentsWithoutReference,
      setPcnDocumentsWithoutReference,
      pcnDocumentsOutOfRange,
      setPcnDocumentsOutOfRange,
      pcnChecksLoading,
      setPcnChecksLoading,
      pcnReportDetails,
      setPcnReportDetails,
      loading,
      setLoading,
      pcnIncludeStornoChecks,
      setPcnIncludeStornoChecks,
      downloadPCNReport,
      handlePCNDownload,
      handlePCNChecks,
      refreshPCNData,
      /* Chat state and functions */
      openChat,
      setOpenChat,
      chatKey,
      setChatKey,
      targetUserId,
      setTargetUserId,
      targetUserType,
      setTargetUserType,
      targetUserName,
      setTargetUserName,
      clarificationAttachContext,
      setClarificationAttachContext,
      handleChatClose,
      handleSendMessageToChat,
      handleSendTextMessageToChat,
      selectUserAndOpenChat,
      pcnRefreshClientDetails,
      setPcnRefreshClientDetails,
      pcnRefreshDetails,
      setPcnRefreshDetails,
      refreshUnprocessedPreview,
      setRefreshUnprocessedPreview,
      openWithholdingTaxModel,
      setOpenWithholdingTaxModel,
      /* Enums refresh callback */
      refreshEnumsCallback,
      setRefreshEnumsCallback,
      /* Enums data */
      accountCardOptions,
      setAccountCardOptions,
      sortCodeIdOptions,
      setSortCodeIdOptions,
      /* callbacks */
      handleDocumentRowClick,
      editModalUpdated,
      setEditModalUpdated,
      referenceSequenceModalOpen,
      setReferenceSequenceModalOpen,
      openReferenceSequenceModal,
      closeReferenceSequenceModal,
      referencesModalOpen,
      openReferencesModal,
      closeReferencesModal,
    }}>
      {children}
    </HamburgerMenuContext.Provider>
  );
};
